import { Box, Paper, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import type {
	ColDef,
	GridOptions,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { blankToEndComparator } from "@/controls/ag-grid/components/GridDefinitions";
import { themeDXT } from "@/setup/ag-grid-license";
import { useM1RollingTableData } from "../hooks/useM1RollingTableData";
import {
	createAverageRow,
	isAverageRow,
	type SummaryRow,
} from "../models/SummaryRow";
import type {
	InspectionWithValues,
	ProfileInspectionsResult,
} from "../../models/Inspection";
import type { RollingSummaryResult } from "../../models/RollingSummary";
import type { ProfileItem } from "@/pages/configuration/models/ProfileItem";

const SUMMARY_HEADER_HEIGHT = 30;
const SUMMARY_ROW_HEIGHT = 32;
const SUMMARY_SCROLLBAR_HEIGHT = 16;
// Header tiers, three samples, one pinned average and grid borders.
export const SUMMARY_GRID_HEIGHT =
	2 * SUMMARY_HEADER_HEIGHT + 4 * SUMMARY_ROW_HEIGHT + 3;
// The theme rowHeight drives cell line-height; keep it in sync with the rowHeight prop.
const summaryTheme = themeDXT.withParams({
	cellHorizontalPadding: 4,
	rowHeight: SUMMARY_ROW_HEIGHT,
});
// This compact summary uses only the shared center alignment, not its action column types.
const summaryDefaults: ColDef<SummaryRow> = {
	comparator: blankToEndComparator,
	filter: false,
	floatingFilter: false,
	sortable: false,
	editable: false,
	resizable: true,
	wrapText: false,
	autoHeight: false,
	wrapHeaderText: false,
	suppressHeaderMenuButton: true,
	headerClass: "ag-center-aligned-header",
	cellClass: "ag-center-aligned-cell",
	cellStyle: { textAlign: "center" },
};
const summaryGroupDefaults = { suppressStickyLabel: true };
const summaryGridOptions: GridOptions<SummaryRow> = {
	rowSelection: {
		mode: "singleRow",
		checkboxes: false,
		enableClickSelection: true,
	},
};
const getSummaryRowId: NonNullable<GridOptions<SummaryRow>["getRowId"]> = ({
	data,
}) =>
	isAverageRow(data) ? "rolling-average" : String(data.ProfileInspectionId);
const emptyInspections: InspectionWithValues[] = [];

interface SummaryProps {
	profileItems?: ProfileItem[];
	inspections?: InspectionWithValues[];
	applicableItemsBySpec?: ProfileInspectionsResult["applicableItemsBySpec"];
	rolling?: RollingSummaryResult;
	rollingError?: string;
	averageSectionLabel?: string;
	error?: string;
	selectDisabled?: boolean;
	onSelect?: (inspection: InspectionWithValues) => void;
	onDelete?: (inspection: InspectionWithValues) => void;
}

export const Summary = ({
	profileItems,
	inspections,
	applicableItemsBySpec,
	rolling,
	rollingError,
	averageSectionLabel,
	error,
	selectDisabled = false,
	onSelect,
	onDelete,
}: SummaryProps = {}) => {
	const { columnDefs } = useM1RollingTableData({
		onEdit: onSelect,
		onDelete,
		disabled: selectDisabled,
		applicableItemsBySpec,
		profileItems,
		inspections,
	});
	// Without inspections every column group is filtered out, leaving a single
	// header tier. Stretch it over both tiers so the grid keeps its height and
	// no blank strip shows up below the pinned average.
	const hasGroupHeaders = columnDefs.some((column) => "children" in column);
	const headerHeight = hasGroupHeaders
		? SUMMARY_HEADER_HEIGHT
		: 2 * SUMMARY_HEADER_HEIGHT;
	const pinnedRows = useMemo(
		() =>
			rolling?.summary ? [createAverageRow(rolling, averageSectionLabel)] : [],
		[rolling, averageSectionLabel],
	);
	const onRowDoubleClicked = useCallback(
		({ data }: RowDoubleClickedEvent<SummaryRow>) => {
			if (!selectDisabled && data && !isAverageRow(data)) onSelect?.(data);
		},
		[selectDisabled, onSelect],
	);

	return (
		<Paper
			component="section"
			elevation={2}
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minWidth: 0,
				minHeight: SUMMARY_GRID_HEIGHT,
				width: "100%",
				overflow: "hidden",
			}}
		>
			{error && (
				<Typography role="alert" sx={{ p: 1 }}>
					{error}
				</Typography>
			)}
			{rollingError && (
				<Typography role="alert" sx={{ p: 1 }}>
					{`Rolling average: ${rollingError}`}
				</Typography>
			)}
			<Box
				sx={{
					flex: "0 0 auto",
					height: SUMMARY_GRID_HEIGHT,
					minWidth: 0,
					// Reserve space only for a visible, non-overlay scrollbar.
					// Otherwise it looks like extra padding below the pinned average.
					"&:has(.ag-body-horizontal-scroll:not(.ag-invisible):not(.ag-scrollbar-invisible))":
						{
							height: SUMMARY_GRID_HEIGHT + SUMMARY_SCROLLBAR_HEIGHT,
						},
				}}
			>
				<AgGridReact<SummaryRow>
					theme={summaryTheme}
					rowData={inspections ?? emptyInspections}
					columnDefs={columnDefs}
					defaultColDef={summaryDefaults}
					defaultColGroupDef={summaryGroupDefaults}
					pinnedBottomRowData={pinnedRows}
					suppressNoRowsOverlay
					headerHeight={headerHeight}
					groupHeaderHeight={SUMMARY_HEADER_HEIGHT}
					rowHeight={SUMMARY_ROW_HEIGHT}
					scrollbarWidth={SUMMARY_SCROLLBAR_HEIGHT}
					onRowDoubleClicked={onRowDoubleClicked}
					getRowId={getSummaryRowId}
					gridOptions={summaryGridOptions}
				/>
			</Box>
		</Paper>
	);
};
